import { useState } from 'react'
import { useAuth } from '@clerk/clerk-react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { AlertTriangle, ArrowLeft, CheckCircle2, Loader2, Mail } from 'lucide-react'
import { Link, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

import { apiFetch } from '../utils/api'

interface SelectedLine {
  packageId: string
  packageName: string
  partida: string
  itemId: string
  description: string
  quantityBaseWithoutWaste: number
  wastePct: number
  quantityToOrder: number
  unit: string
  state: string
  source: string
  confirmationRequired: boolean
  confirmationLabel: string | null
}

interface SelectedDraftResponse {
  id: string
  status: 'DRAFT' | 'SENDING' | 'SENT' | 'FAILED'
  recipientEmail: string | null
  recipientContactId: string | null
  recipientName: string | null
  recipientKind: 'SELF' | 'CONTACT' | null
  messageId: string | null
  sentAt: string | null
  subject: string
  body: string
  lineCount: number
  pendingConfirmationCount: number
  excludedNotSendableCount: number
  deliveryBlocked: boolean
  lines: SelectedLine[]
}

interface ProjectContact {
  id: string
  name: string
  email: string | null
  role: string
  company: string | null
}

function quantity(value: number, isEs: boolean): string {
  return new Intl.NumberFormat(isEs ? 'es' : 'en-US', { maximumFractionDigits: 4 }).format(value)
}

export default function CertifiedSelectionDraftView() {
  const { projectId, draftId } = useParams<{ projectId: string; draftId: string }>()
  const { getToken } = useAuth()
  const { i18n } = useTranslation()
  const isEs = i18n.language.startsWith('es')
  const language = isEs ? 'es' : 'en'
  const queryClient = useQueryClient()
  const queryKey = ['certified-selection-draft', projectId, draftId, language]
  const [recipientSelection, setRecipientSelection] = useState('')

  const query = useQuery<SelectedDraftResponse>({
    queryKey,
    enabled: Boolean(projectId && draftId),
    queryFn: async () => {
      const token = await getToken()
      const response = await apiFetch(
        `/api/projects/${projectId}/certified-purchase-candidates/selection-drafts/${draftId}?lang=${language}`,
        { headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) } },
      )
      if (!response.ok) throw new Error(`HTTP_${response.status}`)
      return response.json() as Promise<SelectedDraftResponse>
    },
  })
  const refresh = () => queryClient.invalidateQueries({ queryKey })

  const contactsQuery = useQuery<ProjectContact[]>({
    queryKey: ['contacts', projectId],
    enabled: Boolean(projectId),
    queryFn: async () => {
      const token = await getToken()
      const response = await apiFetch(`/api/projects/${projectId}/contacts`, {
        headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      })
      if (!response.ok) throw new Error(`HTTP_${response.status}`)
      return response.json() as Promise<ProjectContact[]>
    },
  })

  const saveRecipient = useMutation({
    mutationFn: async () => {
      const token = await getToken()
      const response = await apiFetch(
        `/api/projects/${projectId}/certified-purchase-candidates/selection-drafts/${draftId}?lang=${language}`,
        {
          method: 'PATCH',
          headers: { 'content-type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
          body: JSON.stringify(
            recipientSelection === 'SELF'
              ? { recipientKind: 'SELF' }
              : { recipientKind: 'CONTACT', recipientContactId: recipientSelection },
          ),
        },
      )
      if (!response.ok) throw new Error(`HTTP_${response.status}`)
    },
    onSuccess: refresh,
  })
  const confirm = useMutation({
    mutationFn: async (line: SelectedLine) => {
      const token = await getToken()
      const response = await apiFetch(
        `/api/projects/${projectId}/certified-purchase-candidates/packages/${encodeURIComponent(line.packageId)}/confirmations`,
        {
          method: 'POST',
          headers: { 'content-type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
          body: JSON.stringify({ partida: line.partida, itemId: line.itemId }),
        },
      )
      if (!response.ok) throw new Error(`HTTP_${response.status}`)
    },
    onSuccess: refresh,
  })
  const send = useMutation({
    mutationFn: async () => {
      const token = await getToken()
      const response = await apiFetch(
        `/api/projects/${projectId}/certified-purchase-candidates/selection-drafts/${draftId}/send?lang=${language}`,
        { method: 'POST', headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) } },
      )
      if (!response.ok) throw new Error(`HTTP_${response.status}`)
    },
    onSuccess: refresh,
  })
  const retry = useMutation({
    mutationFn: async () => {
      const token = await getToken()
      const response = await apiFetch(
        `/api/projects/${projectId}/certified-purchase-candidates/selection-drafts/${draftId}/reset`,
        { method: 'POST', headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) } },
      )
      if (!response.ok) throw new Error(`HTTP_${response.status}`)
    },
    onSuccess: refresh,
  })

  if (query.isLoading) return <div className="flex min-h-[320px] items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-[#06539B]" aria-hidden="true" /></div>
  if (query.isError || !query.data || !projectId) {
    return <div className="mx-auto max-w-3xl p-4 sm:p-6"><div className="rounded-2xl border border-red-200 bg-red-50 p-5 text-sm font-bold text-red-900">{isEs ? 'No se pudo cargar el borrador certificado.' : 'The certified draft could not be loaded.'}</div></div>
  }
  const data = query.data
  const contacts = Array.isArray(contactsQuery.data) ? contactsQuery.data.filter((contact) => Boolean(contact.email)) : []
  const supplierContacts = contacts.filter((contact) => contact.role === 'SUPPLIER')
  const otherContacts = contacts.filter((contact) => contact.role !== 'SUPPLIER')
  const selectedRecipient = recipientSelection || (data.recipientKind === 'SELF' ? 'SELF' : (data.recipientContactId || ''))
  const isSaving = saveRecipient.isPending || confirm.isPending || send.isPending || retry.isPending
  const canSend = data.status === 'DRAFT' && Boolean(data.recipientEmail) && data.pendingConfirmationCount === 0 && !isSaving
  return (
    <main className="mx-auto max-w-6xl space-y-5 p-3 sm:p-6" data-testid="certified-selection-draft-view">
      <Link to={`/projects/${projectId}/purchase-candidates?lang=${language}`} className="inline-flex min-h-11 items-center gap-2 rounded-lg px-2 text-sm font-bold text-[#06539B] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#06539B]"><ArrowLeft className="h-4 w-4" aria-hidden="true" />{isEs ? 'Volver a partidas certificadas' : 'Back to certified items'}</Link>
      <header className="rounded-2xl border-2 border-amber-300 bg-amber-50 p-4 sm:p-5">
        <div className="flex items-start gap-3"><Mail className="mt-0.5 h-6 w-6 shrink-0 text-amber-900" aria-hidden="true" /><div><span className="rounded-full bg-amber-900 px-2.5 py-1 text-xs font-black tracking-wide text-white">{isEs ? 'BORRADOR' : 'DRAFT'}</span><h1 className="mt-3 text-xl font-black text-[#1C1A17] sm:text-2xl">{data.subject}</h1></div></div>
        <p className="mt-4 flex items-start gap-2 text-sm font-bold text-amber-950" data-testid="certified-selection-send-lock"><AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />{data.pendingConfirmationCount > 0 ? (isEs ? 'Confirme los renglones pendientes antes de enviar.' : 'Confirm pending lines before sending.') : (isEs ? 'Revise el destinatario y envíe una sola vez.' : 'Review the recipient and send once.')}</p>
      </header>
      <section className="rounded-2xl border border-[#D8E6F4] bg-[#F7FAFF] p-4" data-testid="certified-selection-recipient">
        <h2 className="text-base font-black text-[#1C1A17]">{isEs ? 'Destinatario del borrador' : 'Draft recipient'}</h2>
        <p className="mt-1 text-sm font-semibold text-[#4E4942]">{isEs ? 'Elija un contacto de este proyecto. Se guardará una copia de su nombre y correo en el borrador certificado.' : 'Choose a contact from this project. A copy of their name and email will be saved on the certified draft.'}</p>
        <div className="mt-3 flex flex-col gap-2 sm:flex-row"><select value={selectedRecipient} onChange={(event) => setRecipientSelection(event.target.value)} disabled={data.status !== 'DRAFT' || contactsQuery.isLoading} className="min-h-11 flex-1 rounded-lg border border-[#B9B4AA] bg-white px-3 text-sm disabled:bg-slate-100" data-testid="certified-selection-recipient-select"><option value="">{isEs ? 'Seleccione un destinatario' : 'Select a recipient'}</option><option value="SELF">{isEs ? 'Enviarme a mí' : 'Send to me'}</option>{supplierContacts.length > 0 ? <optgroup label={isEs ? 'Proveedores' : 'Suppliers'}>{supplierContacts.map((contact) => <option key={contact.id} value={contact.id}>{contact.name} ({contact.email}){contact.company ? ` — ${contact.company}` : ''}</option>)}</optgroup> : null}{otherContacts.length > 0 ? <optgroup label={isEs ? 'Otros contactos' : 'Other contacts'}>{otherContacts.map((contact) => <option key={contact.id} value={contact.id}>{contact.name} ({contact.email}){contact.company ? ` — ${contact.company}` : ''}</option>)}</optgroup> : null}</select><button type="button" onClick={() => saveRecipient.mutate()} disabled={!selectedRecipient || isSaving || data.status !== 'DRAFT'} className="min-h-11 rounded-lg bg-[#06539B] px-4 text-sm font-black text-white disabled:opacity-50" data-testid="certified-selection-save-recipient">{isEs ? 'Guardar destinatario' : 'Save recipient'}</button></div>
      </section>
      <section className="rounded-2xl border border-[#D8E6F4] bg-white p-4" data-testid="certified-selection-send">
        <button type="button" onClick={() => send.mutate()} disabled={!canSend} className="min-h-11 rounded-lg bg-[#06539B] px-4 text-sm font-black text-white disabled:cursor-not-allowed disabled:opacity-50" data-testid="certified-selection-send-button">{isEs ? 'Enviar borrador' : 'Send draft'}</button>
        {data.status === 'FAILED' && data.messageId === null ? <button type="button" onClick={() => retry.mutate()} disabled={isSaving} className="ml-2 min-h-11 rounded-lg border border-[#06539B] px-4 text-sm font-black text-[#06539B] disabled:opacity-50" data-testid="certified-selection-retry-send">{isEs ? 'Reintentar' : 'Retry'}</button> : null}
        {send.isError ? <p className="mt-2 text-sm font-bold text-red-800">{isEs ? 'No se pudo enviar el borrador.' : 'The draft could not be sent.'}</p> : null}
      </section>
      <section className="overflow-hidden rounded-2xl border border-[#ECEAE4] bg-white"><div className="border-b border-[#ECEAE4] bg-[#F8F7F4] px-4 py-3"><strong>{data.lineCount} {isEs ? 'renglones seleccionados' : 'selected lines'}</strong> · <span>{data.pendingConfirmationCount} {isEs ? 'por confirmar' : 'pending'}</span></div><div className="divide-y divide-[#ECEAE4]">{data.lines.map((line) => <article key={`${line.partida}:${line.itemId}`} className="grid gap-3 px-4 py-4 sm:grid-cols-[minmax(0,1fr)_170px]" data-testid="certified-selection-draft-line"><div><div className="flex flex-wrap items-center gap-2"><span className="rounded-full border border-slate-300 bg-slate-100 px-2 py-1 text-[10px] font-black text-slate-900">{line.state}</span>{line.confirmationRequired ? <button type="button" onClick={() => confirm.mutate(line)} disabled={isSaving} className="min-h-11 rounded-full border border-amber-600 bg-amber-50 px-3 text-[11px] font-black text-amber-950 disabled:opacity-60" data-testid="certified-selection-confirm-line">{isEs ? 'Confirmar renglón' : 'Confirm line'}</button> : <span className="rounded-full border border-emerald-300 bg-emerald-50 px-2 py-1 text-[10px] font-black text-emerald-950">{isEs ? 'Confirmado o verificado' : 'Confirmed or verified'}</span>}<span className="font-mono text-[10px] font-bold text-[#6B655C]">{line.packageId} · {line.partida} · {line.itemId}</span></div><h3 className="mt-2 text-sm font-extrabold text-[#1C1A17]">{line.description}</h3><p className="mt-1 text-xs font-semibold text-[#4E4942]"><strong>{isEs ? 'Fuente' : 'Source'}:</strong> {line.source}</p></div><div className="rounded-xl bg-[#F7FAFF] p-3"><p className="text-[10px] font-bold uppercase tracking-wide text-[#4E4942]">{isEs ? 'Cantidad a ordenar' : 'Quantity to order'}</p><p className="mt-1 text-base font-black text-[#06539B]">{quantity(line.quantityToOrder, isEs)} {line.unit}</p><p className="mt-1 text-xs font-semibold text-[#6B655C]">{isEs ? 'Cantidad base' : 'Base quantity'}: {quantity(line.quantityBaseWithoutWaste, isEs)} · {isEs ? 'Merma' : 'Waste'}: {quantity(line.wastePct, isEs)}%</p></div></article>)}</div></section>
      <footer className="flex items-start gap-2 rounded-xl border border-emerald-200 bg-emerald-50 p-4 text-xs font-semibold text-emerald-950"><CheckCircle2 className="h-4 w-4 shrink-0" aria-hidden="true" /><span>{isEs ? 'Fuente exclusiva: renglones seleccionados del snapshot certificado. No se consultaron fuentes legadas ni se creó un envío.' : 'Exclusive source: selected rows from the certified snapshot. No legacy source was queried and no delivery was created.'}</span></footer>
    </main>
  )
}
